import React, { useState, useEffect, useRef } from 'react';
import StorylineOverlay from './StorylineOverlay';
import MusicalCountsOverlay from './MusicalCountsOverlay';
import TechnicalNotesOverlay from './TechnicalNotesOverlay';
import storylineData from '../data/storylineNotes.json';
import musicalCountsData from '../data/musicalCounts.json';
import technicalNotesData from '../data/technicalNotes.json';

const VideoOverlay = ({ 
  currentTime, 
  showStoryline, 
  showMusicalCounts, 
  showTechnicalNotes,
  storylineOnVideo,
  musicalCountsOnVideo,
  technicalNotesOnVideo,
  videoControls,
  textSize = 100
}) => {
  // Use state to track the heights of each overlay
  const [overlayHeights, setOverlayHeights] = useState({
    storyline: 0,
    musicalCounts: 0,
    technicalNotes: 0
  });
  
  // Refs for measuring overlay heights
  const storylineRef = useRef(null);
  const musicalCountsRef = useRef(null);
  const technicalNotesRef = useRef(null);
  
  // State to track if there are notes at the current timestamp
  const [hasStorylineNotes, setHasStorylineNotes] = useState(false);
  const [hasMusicalCounts, setHasMusicalCounts] = useState(false);
  const [hasTechnicalNotes, setHasTechnicalNotes] = useState(false);
  
  // Check if there are notes at the current timestamp
  useEffect(() => {
    // Buffer time (in seconds) to keep a note visible after its timestamp
    const bufferTime = 5;
    
    // Check for storyline notes
    const storylineNote = storylineData.find(note => {
      return currentTime >= note.seconds && currentTime < note.seconds + bufferTime;
    });
    setHasStorylineNotes(!!storylineNote);
    
    // Check for musical counts
    let closestCount = null;
    let smallestDifference = Infinity;
    
    for (const count of musicalCountsData) {
      if (count.seconds <= currentTime) {
        const difference = currentTime - count.seconds;
        if (difference < smallestDifference) {
          smallestDifference = difference;
          closestCount = count;
        }
      }
    }
    
    setHasMusicalCounts(!!closestCount && smallestDifference <= 1);
    
    // Check for technical notes
    const technicalNote = technicalNotesData.find(note => {
      return currentTime >= note.seconds && currentTime < note.seconds + bufferTime;
    });
    setHasTechnicalNotes(!!technicalNote);
  }, [currentTime]);
  
  // Check if any overlays should be displayed on the video
  const hasOverlays = 
    (showStoryline && storylineOnVideo && hasStorylineNotes) || 
    (showMusicalCounts && musicalCountsOnVideo && hasMusicalCounts) || 
    (showTechnicalNotes && technicalNotesOnVideo && hasTechnicalNotes);
  
  // Update heights when overlays change
  useEffect(() => {
    if (!hasOverlays) {
      return;
    }
    
    // Function to update heights
    const updateHeights = () => {
      const newHeights = { ...overlayHeights };
      
      if (storylineRef.current) {
        newHeights.storyline = storylineRef.current.offsetHeight;
      }
      
      if (musicalCountsRef.current) {
        newHeights.musicalCounts = musicalCountsRef.current.offsetHeight;
      }
      
      if (technicalNotesRef.current) {
        newHeights.technicalNotes = technicalNotesRef.current.offsetHeight;
      }
      
      setOverlayHeights(newHeights);
    };
    
    // Update heights immediately and then again after a delay
    updateHeights();
    
    // Update heights after a short delay to ensure DOM is updated
    const timer = setTimeout(updateHeights, 100);
    
    // Set up a mutation observer to detect changes in the DOM
    const observer = new MutationObserver(updateHeights);
    
    // Observe changes to the overlays
    if (storylineRef.current) {
      observer.observe(storylineRef.current, { childList: true, subtree: true });
    }
    
    if (musicalCountsRef.current) {
      observer.observe(musicalCountsRef.current, { childList: true, subtree: true });
    }
    
    if (technicalNotesRef.current) {
      observer.observe(technicalNotesRef.current, { childList: true, subtree: true });
    }
    
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [showStoryline, showMusicalCounts, showTechnicalNotes, currentTime, hasOverlays]);
  
  // Calculate positions based on which overlays are active and their heights
  const getPositions = () => {
    // Fixed positions for each overlay type
    const positions = {
      musicalCounts: { top: '1rem' }, // Music always at top
      storyline: { top: '1rem' },
      technicalNotes: { top: '1rem' }
    };
    
    // Fixed spacing between overlays (reduced from 2rem to 0.5rem)
    const spacing = '0.5rem';
    
    // Reorder overlays to ensure music is always first
    const orderedOverlays = [];
    
    // First check if music is active
    if (showMusicalCounts && musicalCountsOnVideo && hasMusicalCounts) {
      orderedOverlays.push('musicalCounts');
    }
    
    // Then add storyline if active
    if (showStoryline && storylineOnVideo && hasStorylineNotes) {
      orderedOverlays.push('storyline');
    }
    
    // Then add technical notes if active
    if (showTechnicalNotes && technicalNotesOnVideo && hasTechnicalNotes) {
      orderedOverlays.push('technicalNotes');
    }
    
    // Position each overlay based on the ones above it
    let currentTop = '1rem'; // Start at top with less space
    
    orderedOverlays.forEach((overlay, index) => {
      if (index === 0) {
        // First overlay starts at the top
        positions[overlay].top = currentTop;
        // Set z-index for proper stacking (higher index = higher z-index)
        positions[overlay].zIndex = 20;
      } else {
        // Subsequent overlays positioned below the previous ones
        const previousOverlay = orderedOverlays[index - 1];
        const previousHeight = overlayHeights[previousOverlay] || 0;
        
        // Calculate new top position
        currentTop = `calc(${currentTop} + ${previousHeight}px + ${spacing})`;
        positions[overlay].top = currentTop;
        // Set z-index for proper stacking (higher index = higher z-index)
        positions[overlay].zIndex = 20 + index;
      }
    });
    
    return positions;
  };
  
  if (!hasOverlays) {
    return null;
  }
  
  // Create a style object for the text size
  const textSizeStyle = {
    fontSize: `${textSize}%`
  };
  
  // Get positions and check if multiple overlays are active
  const positions = getPositions();
  
  // Reorder overlays to ensure music is always first
  const orderedOverlays = [];
  
  // First check if music is active
  if (showMusicalCounts && musicalCountsOnVideo && hasMusicalCounts) {
    orderedOverlays.push('musicalCounts');
  }
  
  // Then add storyline if active
  if (showStoryline && storylineOnVideo && hasStorylineNotes) {
    orderedOverlays.push('storyline');
  }
  
  // Then add technical notes if active
  if (showTechnicalNotes && technicalNotesOnVideo && hasTechnicalNotes) {
    orderedOverlays.push('technicalNotes');
  }
  
  // Check if multiple overlays are active
  const multipleOverlays = orderedOverlays.length > 1;
  
  return (
    <div className="absolute inset-0 pointer-events-none">
      {/* Storyline overlay - only render if there are notes */}
      {showStoryline && storylineOnVideo && hasStorylineNotes && (
        <div 
          ref={storylineRef}
          className={`absolute left-0 right-0 p-3 pt-6 pb-4 text-white pointer-events-auto storyline-overlay ${multipleOverlays ? 'stacked-overlay' : ''}`}
          style={{ 
            top: positions.storyline.top, 
            position: 'absolute', 
            bottom: 'auto',
            zIndex: positions.storyline.zIndex,
            fontSize: `${textSize}%`
          }}
        >
          <h3 className="text-sm font-semibold text-blue-300 mb-2 absolute left-3 top-1">Storyline</h3>
          <div className="mt-2">
            <StorylineOverlay currentTime={currentTime} inPanel={false} videoControls={videoControls} textSize={textSize} />
          </div>
        </div>
      )}
      
      {/* Musical counts overlay - only render if there are notes */}
      {showMusicalCounts && musicalCountsOnVideo && hasMusicalCounts && (
        <div 
          ref={musicalCountsRef}
          className={`absolute left-0 right-0 p-3 pt-6 pb-4 text-white pointer-events-auto musical-count-overlay ${multipleOverlays ? 'stacked-overlay' : ''}`}
          style={{ 
            top: positions.musicalCounts.top, 
            position: 'absolute', 
            bottom: 'auto',
            zIndex: positions.musicalCounts.zIndex,
            fontSize: `${textSize}%`
          }}
        >
          <h3 className="text-sm font-semibold text-green-300 mb-2 absolute left-3 top-1">Music</h3>
          <div className="mt-4">
            <MusicalCountsOverlay currentTime={currentTime} inPanel={false} textSize={textSize} />
          </div>
        </div>
      )}
      
      {/* Technical notes overlay - only render if there are notes */}
      {showTechnicalNotes && technicalNotesOnVideo && hasTechnicalNotes && (
        <div 
          ref={technicalNotesRef}
          className={`absolute left-0 right-0 p-3 pt-6 pb-4 text-white pointer-events-auto technical-note-overlay ${multipleOverlays ? 'stacked-overlay' : ''}`}
          style={{ 
            top: positions.technicalNotes.top, 
            position: 'absolute', 
            bottom: 'auto',
            zIndex: positions.technicalNotes.zIndex,
            fontSize: `${textSize}%`
          }}
        >
          <h3 className="text-sm font-semibold text-yellow-300 mb-2 absolute left-3 top-1">Technical</h3>
          <div className="mt-2">
            <TechnicalNotesOverlay currentTime={currentTime} inPanel={false} textSize={textSize} />
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoOverlay;
